/*  Footer — iter100.151 slimmed 3-column layout.
    ────────────────────────────────────────────────────────────────
    Everything on this footer is admin-editable via the CMS "Site Footer"
    chip. Three columns + bottom bar:

      LEFT   ├─ Brand + tagline + trust metric + contact block + social row
      CENTER ├─ Start Your Smile Journey (patient-facing link groups)
      RIGHT  ├─ For Providers (doctor + company + legal link groups)
      BOTTOM ├─ Copyright + trust signals + Doctor Portal link

    Link groups are stored as structured JSON in
    `footer.patient_sections_json` and `footer.provider_sections_json`
    so marketing can add/remove/re-label links without a deploy.

    iter100.151 — Removed the newsletter signup block; B2B medical
    doctor portals don't ship consumer-style newsletter capture, and
    it added noise + a fake POST endpoint. The rest of the footer
    stays the same shape.
*/
import React from "react";
import { Link } from "react-router-dom";
import {
    InstagramLogo, LinkedinLogo, TwitterLogo, YoutubeLogo, FacebookLogo,
    Phone, EnvelopeSimple, MapPin, Star, Stethoscope, Smiley, ShieldCheck,
    Certificate, Heart,
} from "@phosphor-icons/react";
import InvisiqueLogo from "@/components/site/Logo";
import { portalUrl } from "@/lib/host-mode";
import { useCms } from "@/lib/useCms";


/* Icon map used by `bottom_metrics_json.icon`. Keep in sync with the
   list in the CMS field description so admins know which strings work. */
const METRIC_ICONS = {
    Star, Stethoscope, Smiley, ShieldCheck, Certificate, Heart,
};


function parseJson(str, fallback) {
    if (!str) return fallback;
    try {
        const v = JSON.parse(str);
        return Array.isArray(v) ? v : fallback;
    } catch {
        return fallback;
    }
}


export const Footer = () => {
    const { t } = useCms("footer");
    const { t: tc } = useCms("contact_info");
    const { t: ts } = useCms("social_links");
    const year = new Date().getFullYear();

    /* ── Left column data ── */
    const brandStatement = t("tagline", "");
    const trustMetric    = t("trust_metric_line", "");

    /* ── Contact block (existing CMS page) ── */
    const phone     = tc("phone", "");
    const phoneHref = tc("phone_intl_href", "");
    const email     = tc("email_support", "");
    const address   = tc("hq_address", "");
    const hours     = tc("hq_hours", "");

    /* ── Social row (existing CMS page) — only render channels with URLs ── */
    const socialLinks = [
        { url: ts("instagram_url", ""), icon: InstagramLogo, label: "instagram",  testid: "footer-social-instagram" },
        { url: ts("linkedin_url", ""),  icon: LinkedinLogo,  label: "linkedin",   testid: "footer-social-linkedin" },
        { url: ts("twitter_url", ""),   icon: TwitterLogo,   label: "twitter",    testid: "footer-social-twitter" },
        { url: ts("youtube_url", ""),   icon: YoutubeLogo,   label: "youtube",    testid: "footer-social-youtube" },
        { url: ts("facebook_url", ""),  icon: FacebookLogo,  label: "facebook",   testid: "footer-social-facebook" },
    ].filter((s) => s.url);

    /* ── Column titles + structured link sections ── */
    const patientTitle  = t("patient_column_title",  "Start Your Smile Journey");
    const providerTitle = t("provider_column_title", "For Providers");
    const patientSections  = parseJson(t("patient_sections_json", ""),  []);
    const providerSections = parseJson(t("provider_sections_json", ""), []);

    /* ── Bottom bar ── */
    const bottomLine1  = (t("bottom_left_line1", "© {year} Invisique™. All rights reserved.")).replace(/\{year\}/g, year);
    const bottomLine2  = t("bottom_left_line2", "");
    const bottomMetrics = parseJson(t("bottom_metrics_json", ""), []);

    return (
        <footer className="border-t border-border bg-secondary/50" data-testid="site-footer">
            {/* ── Main 3-column grid ── */}
            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid lg:grid-cols-12 gap-10">

                {/* LEFT — Brand */}
                <div className="lg:col-span-4 space-y-5">
                    <InvisiqueLogo showTagline />
                    {brandStatement && (
                        <p className="text-muted-foreground max-w-md text-sm leading-relaxed" data-testid="footer-tagline">
                            {brandStatement}
                        </p>
                    )}
                    {trustMetric && (
                        <div className="inline-flex items-start gap-2 rounded-xl border border-primary/20 bg-primary/[0.04] px-3 py-2 text-[12px] text-foreground max-w-md" data-testid="footer-trust-metric">
                            <ShieldCheck size={14} weight="fill" className="text-primary shrink-0 mt-0.5" />
                            <span className="leading-snug">{trustMetric}</span>
                        </div>
                    )}

                    {/* Contact block */}
                    {(phone || email || address) && (
                        <ul className="space-y-1.5 text-sm text-muted-foreground" data-testid="footer-contact-block">
                            {phone && (
                                <li className="flex items-center gap-2" data-testid="footer-phone">
                                    <Phone size={13} weight="fill" className="text-primary shrink-0" />
                                    {phoneHref ? <a href={phoneHref} className="hover:text-primary">{phone}</a> : <span>{phone}</span>}
                                </li>
                            )}
                            {email && (
                                <li className="flex items-center gap-2" data-testid="footer-email">
                                    <EnvelopeSimple size={13} weight="fill" className="text-primary shrink-0" />
                                    <a href={`mailto:${email}`} className="hover:text-primary">{email}</a>
                                </li>
                            )}
                            {address && (
                                <li className="flex items-start gap-2" data-testid="footer-address">
                                    <MapPin size={13} weight="fill" className="text-primary shrink-0 mt-0.5" />
                                    <span>{address}</span>
                                </li>
                            )}
                            {hours && (
                                <li className="text-[11px] text-muted-foreground/70 ml-5" data-testid="footer-hours">{hours}</li>
                            )}
                        </ul>
                    )}

                    {/* Social row */}
                    {socialLinks.length > 0 && (
                        <div className="flex gap-2.5" data-testid="footer-social-row">
                            {socialLinks.map(({ url, icon: Icon, label, testid }) => (
                                <a
                                    key={label}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={label}
                                    className="w-9 h-9 rounded-lg border border-border bg-white flex items-center justify-center hover:border-primary hover:text-primary hover:-translate-y-0.5 transition-all"
                                    data-testid={testid}
                                >
                                    <Icon size={17} />
                                </a>
                            ))}
                        </div>
                    )}
                </div>

                {/* CENTER — Patient */}
                <div className="lg:col-span-4">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary mb-5" data-testid="footer-patient-title">{patientTitle}</div>
                    <div className="space-y-6" data-testid="footer-patient-sections">
                        {patientSections.map((section) => (
                            <div key={section.heading}>
                                <div className="overline mb-2.5 text-muted-foreground">{section.heading}</div>
                                <ul className="space-y-2 text-sm">
                                    {(section.links || []).map((l) => (
                                        <li key={`${section.heading}-${l.label}`}>
                                            <FooterLink href={l.href} label={l.label} />
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                {/* RIGHT — Provider */}
                <div className="lg:col-span-4">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary mb-5" data-testid="footer-provider-title">{providerTitle}</div>
                    <div className="space-y-6" data-testid="footer-provider-sections">
                        {providerSections.map((section) => (
                            <div key={section.heading}>
                                <div className="overline mb-2.5 text-muted-foreground">{section.heading}</div>
                                <ul className="space-y-2 text-sm">
                                    {(section.links || []).map((l) => (
                                        <li key={`${section.heading}-${l.label}`}>
                                            <FooterLink href={l.href} label={l.label} />
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

            </div>

            {/* ── Bottom bar ── */}
            <div className="border-t border-border bg-secondary/70">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
                    <div className="text-xs text-muted-foreground" data-testid="footer-bottom-left">
                        <div className="text-foreground/80 font-medium">{bottomLine1}</div>
                        {bottomLine2 && <div className="mt-0.5 text-[11px] leading-snug">{bottomLine2}</div>}
                    </div>

                    {bottomMetrics.length > 0 && (
                        <div className="flex items-center flex-wrap gap-x-4 gap-y-2 text-[11.5px] text-muted-foreground" data-testid="footer-bottom-metrics">
                            {bottomMetrics.map((m, i) => {
                                const Icon = METRIC_ICONS[m.icon] || Star;
                                return (
                                    <div key={i} className="inline-flex items-center gap-1.5">
                                        <Icon size={12} weight="fill" className="text-primary" />
                                        <span className="font-medium text-foreground/80">{m.label}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="text-[11px] text-muted-foreground" data-testid="footer-doctor-portal">
                        <a href={portalUrl("/doctor-login")} className="hover:text-primary underline underline-offset-2">
                            Doctor Portal
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};


/* Small link helper — routes internal /paths through react-router,
   external ones (http/https/mailto/tel) render as plain <a>. */
function FooterLink({ href, label }) {
    if (!href) return <span className="text-muted-foreground">{label}</span>;
    if (/^(https?:|mailto:|tel:)/i.test(href)) {
        return <a href={href} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">{label}</a>;
    }
    return <Link to={href} className="text-muted-foreground hover:text-primary transition-colors">{label}</Link>;
}


export default Footer;
